import { AutocompleteFormComponent } from "components/Form/components/Autocomplete/AutocompleteFormComponent";
import { getUpdatedData } from "components/Form/components/SchemaForm/SchemaFormService";
import { SchemaFieldTypeExtended } from "config/config";
import { CustomFilterValues } from "containers/AdvancedSearch/AdvancedSearch";
import { ValueLabelPair } from "containers/ExecutionFilter/interfaces";
import { ExtendedFilterFieldFragment } from "containers/Filter/FilterBuilder/components/ExtendedFilterField";
import { DeleteFieldButton } from "containers/Filter/FilterBuilder/components/FilterFragment";
import { ValueField } from "containers/Filter/FilterBuilder/components/ValueField";
import { useMemo, useState } from "react";
import { Field } from "react-final-form";
import { SingleField } from "utils/utils";
import {
    eqTrueOp,
    getOperatorList,
    isArrayOperator,
    isBooleanTypeOperator,
    isNullableOperator,
} from "../utils";

interface FilterFieldProps {
    elementData: SingleField;
    index: number;
    matchingSchema?: SchemaFieldTypeExtended;
    filteredFieldList: ValueLabelPair[];
    parentPath: string;
    setData: (value: CustomFilterValues) => void;
    formData: CustomFilterValues;
    parentData: CustomFilterValues[];
    matchingFields: SchemaFieldTypeExtended[];
    deleteGroup?: () => void;
}

/**
 * Component for rendering individual filter fields.
 * Handles field selection, operator selection, value input, and field deletion.
 * Supports both regular fields and extended fields with foreign key relationships.
 *
 * @param {FilterFieldProps} props - Component props
 * @returns {JSX.Element} Rendered filter field
 */
export const FilterField = ({
    elementData,
    index,
    matchingSchema = {} as SchemaFieldTypeExtended,
    filteredFieldList,
    parentPath,
    setData,
    formData,
    parentData,
    matchingFields,
    deleteGroup,
}: FilterFieldProps) => {
    // Construct paths for nested form fields
    const childPath = `${parentPath}[${index}]`;
    const childPathField = `${childPath}.field`;
    const childPathOp = `${childPath}.op`;
    const childPathValue = `${childPath}.value`;

    /**
     * Handles deletion of this filter field.
     * If this is the last field in the group, deletes the entire group.
     */
    const onDelete = () => {
        const newValue = [...parentData];
        newValue.splice(index, 1);
        if (newValue.length) {
            // Update the parent group with the remaining fields
            setData(getUpdatedData(formData, parentPath, newValue));
        } else {
            // Delete the entire group if no fields remain
            deleteGroup?.();
        }
    };

    // State for validation error messages
    const [errorMessage, setErrorMessage] = useState(undefined);
    // State for the current matching schema (can be updated by ExtendedFilterFieldFragment)
    const [currentMatchingSchema, setCurrentMatchingSchema] = useState(null);

    // Use current matching schema if available, otherwise fall back to the prop
    const finalSchema = useMemo(
        () => currentMatchingSchema ?? matchingSchema,
        [currentMatchingSchema, matchingSchema]
    );

    return (
        <div className="filterField">
            {/* Field selection dropdown */}
            <Field
                name={childPathField}
                validate={(value) => {
                    // Only validate as required for non-extended fields
                    if (!finalSchema.isExtended && !value) {
                        return "Required.";
                    } else {
                        return undefined;
                    }
                }}
                key={`${finalSchema.isExtended}`}
            >
                {({ input, meta }) => {
                    return (
                        <ExtendedFilterFieldFragment
                            input={input}
                            meta={meta}
                            matchingSchema={matchingSchema}
                            fieldNameIndex={0}
                            isRoot={true}
                            elementData={elementData}
                            setData={setData}
                            formData={formData}
                            elementPath={childPath}
                            matchingFields={matchingFields}
                            filteredFieldList={filteredFieldList}
                            setCurrentMatchingSchema={setCurrentMatchingSchema}
                            partialPath={childPathField}
                        />
                    );
                }}
            </Field>

            {/* Operator selection dropdown - only show for non-extended fields with a selected field */}
            {!finalSchema.isExtended && elementData.field && (
                <div className="fieldOperator">
                    <Field name={childPathOp}>
                        {({ input, meta }) => (
                            <AutocompleteFormComponent
                                fieldProps={{
                                    label: "",
                                    options: {
                                        list: getOperatorList(finalSchema),
                                    },
                                    customKey: `${input.name}-${elementData.field}`,
                                    fieldWidth: 120,
                                }}
                                input={{
                                    ...input,
                                    onChange: (value) => {
                                        // Handle array operators (in, notin) - convert value to array
                                        if (isArrayOperator(value)) {
                                            const newFieldValue = Array.isArray(elementData.value)
                                                ? elementData.value
                                                : [];
                                            setData(
                                                getUpdatedData(formData, childPath, {
                                                    field: elementData.field,
                                                    op: value,
                                                    value: newFieldValue,
                                                })
                                            );
                                        } else if (isArrayOperator(elementData.op)) {
                                            // If switching from array operator, reset value to null
                                            setData(getUpdatedData(formData, childPathValue, null));
                                        } else {
                                            // Handle boolean operators specially
                                            if (isBooleanTypeOperator(value)) {
                                                setData(
                                                    getUpdatedData(formData, childPath, {
                                                        field: elementData.field,
                                                        op: "eq",
                                                        value: value === eqTrueOp ? true : false,
                                                    })
                                                );
                                            } else {
                                                // Regular operator change
                                                setData(
                                                    getUpdatedData(formData, childPathOp, value)
                                                );
                                            }
                                        }
                                    },
                                    // For boolean fields, show eqTrue/eqFalse instead of the actual operator
                                    value:
                                        finalSchema?.type !== "bool"
                                            ? elementData.op
                                            : elementData.value === false
                                            ? "eqFalse"
                                            : "eqTrue",
                                }}
                                meta={meta}
                                fieldAttr={{ placeholder: "operator" }}
                            />
                        )}
                    </Field>
                </div>
            )}
            {/* Value input field - only show when operator is selected and not a boolean operator */}
            {elementData.op &&
                !isBooleanTypeOperator(elementData.op) &&
                !isNullableOperator(elementData.op) && (
                    <Field
                        name={childPathValue}
                        validate={(value) => {
                            if (
                                !finalSchema.nullable &&
                                (value === null || value === undefined || value === "")
                            ) {
                                return "Required";
                            }
                            return errorMessage;
                        }}
                        key={`${errorMessage}-${elementData.op}`}
                    >
                        {({ input, meta }) => (
                            <ValueField
                                input={input}
                                meta={meta}
                                value={elementData.value}
                                matchingSchema={finalSchema}
                                childPathValue={childPathValue}
                                setData={setData}
                                formData={formData}
                                setErrorMessage={setErrorMessage}
                                op={elementData.op}
                                elementDataField={elementData.field}
                            />
                        )}
                    </Field>
                )}
            <div className="deleteFieldButtonContainer">
                <DeleteFieldButton onDelete={onDelete} />
            </div>
        </div>
    );
};
